<!-- Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. -->

<ng-container
	*ngIf="editor.editorMode === EditorMode.Embed"
	[ngTemplateOutlet]="dialogContent"
></ng-container>

<mat-grid-list
	*ngIf="editor.editorMode !== EditorMode.Embed"
	cols="5"
	gutterSize="15"
	[rowHeight]="customHeight"
>
	<mat-grid-tile colspan="2" rowspan="1">
		<ng-container [ngTemplateOutlet]="dialogContent"></ng-container>
	</mat-grid-tile>
	<mat-divider vertical="true"></mat-divider>
	<mat-grid-tile colspan="3" rowspan="1">
		<ng-container *ngIf="editor.inAddItem" [ngTemplateOutlet]="addEditor"></ng-container>
		<ng-container *ngIf="editor.itemInEdit" [ngTemplateOutlet]="updateEditor"></ng-container>
	</mat-grid-tile>
</mat-grid-list>

<ng-template #dialogContent>
	<!-- search bar -->
	<div class="full-width" [ngClass]="{ 'full-height': editor.editorMode !== EditorMode.Embed }">
		<mat-toolbar
			class="background-white"
			[ngClass]="{ 'mat-toolbar-dense': editor.editorMode !== EditorMode.Embed }"
		>
			<mat-toolbar-row *ngIf="editor.editorMode === EditorMode.Embed">
				<mat-form-field subscriptSizing="dynamic" class="full-width">
					<mat-icon matIconPrefix>search</mat-icon>
					<input
						matInput
						type="search"
						automationId="searchBar"
						[placeholder]="strings.PROJECT_ITEM_SEARCH(itemType.pluralDisplay.toLowerCase())"
						[formControl]="searchControl"
					/>
				</mat-form-field>
			</mat-toolbar-row>
			<mat-toolbar-row *ngIf="editor.isAdmin" class="control-toolbar space-between">
				<button mat-button class="icon-button-with-text" (click)="editor.startAdd()" automationId="addItem">
					<mat-icon>add_circle</mat-icon>
					{{ itemType.singularDisplay }}
				</button>
				<div *ngIf="editor.getItemListFilters() | async as filters">
					<button mat-button [matMenuTriggerFor]="menu">
						<span *ngIf="editor.itemFilter">{{ editor.itemFilter }}</span>
						<mat-icon iconPositionEnd>filter_alt</mat-icon>
					</button>
					<mat-menu #menu="matMenu">
						<button *ngFor="let filter of filters" mat-menu-item (click)="editor.applyFilter(filter)">
							<mat-icon>{{ editor.itemFilter === filter ? 'check' : '' }}</mat-icon>
							<span>{{ filter }}</span>
						</button>
					</mat-menu>
				</div>
				<div
					class="palette-edit-control color-picker-toggle-hidden"
					*ngIf="editor.getItemPaletteColors() | async"
				>
					<span class="item-edit-color-picker">
						<color-picker
							#colorPicker
							[alpha]="0.1"
							[format]="'hex'"
							[formControl]="colorControl"
							(beforeShow)="editor.itemInEdit = null; editor.inAddItem = false"
							(show)="editor.itemInEdit = null; editor.inAddItem = false"
							[editLabelText]="strings.COLOR_PICKER_TITLE_LABEL"
							[addColorText]="'Add ' + itemType.type + ' Color'"
							(hide)="isColorPickerShowing = false"
							[options]="{
								showAlpha: false,
								showInitial: false,
								showPalette: true,
								showPaletteOnly: false,
								showInput: true,
								preferredFormat: 'hex',
								usedColors: editor.getUsedColors() | async,
								noAutoClose: true,
								allowEmpty: false,
								showAddColorButton: true,
								hideSaveButton: true,
								forceShow: isColorPickerShowing,
								palette: editor.getItemPaletteColors() | async,
								onAddColor: editor.getAddColorCallback()
							}"
						>
						</color-picker>
					</span>
					<button
						mat-button
						class="icon-button-with-text"
						(click)="isColorPickerShowing = !isColorPickerShowing"
						[ngClass]="{ 'color-picker-less-margin': itemType.type === ITEM_TYPE.Location }"
					>
						<mat-icon>edit</mat-icon>
						{{ strings.PROJECT_MAST_COLOR_LIST_EDIT(itemType.singularDisplay) }}
					</button>
				</div>
			</mat-toolbar-row>
		</mat-toolbar>

		<!-- item list. [itemSize] dictates how tall in pixels each row in the list is -->
		<cdk-virtual-scroll-viewport
			#viewPort
			*ngIf="itemList && !hideItemList"
			itemSize="1"
			class="full-width item-list-scroll-pane"
			[ngStyle]="
				editor.editorMode === EditorMode.Embed
					? editor.isAdmin
						? { height: '420px' }
						: { height: '470px' }
					: editor.isAdmin
					? { height: '480px' }
					: { height: '530px' }
			"
		>
			<ng-container
				*ngIf="editor.editorMode === EditorMode.Embed && editor.inAddItem"
				[ngTemplateOutlet]="addEditor"
			></ng-container>
			<mat-divider></mat-divider>

			<ng-container *ngIf="itemList.length === 0 && !editor.inAddItem" [ngTemplateOutlet]="emptyItemList">
			</ng-container>

			<div
				*ngIf="itemList.length > 0"
				cdkDropList
				[cdkDropListDisabled]="!enableItemDragAndDrop"
				(cdkDropListDropped)="editor.doReorder($event, itemList)"
			>
				<div *cdkVirtualFor="let item of itemList" cdkDrag>
					<ng-container [ngTemplateOutlet]="itemListEntry" [ngTemplateOutletContext]="{ item: item }">
					</ng-container>
				</div>
			</div>
		</cdk-virtual-scroll-viewport>
	</div>
</ng-template>

<ng-template #itemListEntry let-item="item">
	<div *ngIf="editor.getItemDisplayTitle(item)" class="full-width">
		<label class="item-title">{{ editor.getItemDisplayTitle(item) }}</label>
		<mat-divider></mat-divider>
	</div>

	<div
		[ngClass]="{ 'edit-item-panel': editor.itemInEdit && item === editor.itemInEdit }"
		*ngIf="
			(!searchControl.value || editor.doesItemMatchSearch(item, searchControl.value)) &&
			editor.doesItemMatchFilter(item)
		"
	>
		<app-list-item
			(itemClickedEvent)="editor.startEdit($event)"
			[item]="item"
			[itemData]="editor.getListItemData(item)"
			[dragAndDropEnabled]="enableItemDragAndDrop"
			[highlight]="highlightItemInEdit && item === editor.itemInEdit"
		>
		</app-list-item>

		<mat-card-content
			class="embed-edit-panel-container"
			*ngIf="editor.editorMode === EditorMode.Embed && editor.itemInEdit && item === editor.itemInEdit"
		>
			<ng-container [ngTemplateOutlet]="updateEditor"></ng-container>
		</mat-card-content>

		<mat-divider></mat-divider>
	</div>
</ng-template>

<ng-template #emptyItemList>
	<div class="empty-item-list-container">
		<mat-icon *ngIf="itemType.icon" class="empty-item-list-logo">{{ itemType.icon }} </mat-icon>
		<p class="empty-item-list-title">
			{{ strings.PROJECT_EMPTY_ITEM_TITLE(itemType.pluralDisplay.toLowerCase()) }}
		</p>
		<p>{{ strings.PROJECT_EMPTY_ITEM_MESSAGE(itemType.singularDisplay.toLowerCase()) }}</p>
	</div>
</ng-template>

<ng-template #addEditor>
	<ng-content select="[itemCreator]"></ng-content>
</ng-template>

<ng-template #updateEditor>
	<ng-content select="[itemEditor]"></ng-content>
</ng-template>
